/* ── Career Highs Section ──────────────────────────────────────────────────
   Reusable component used on both PlayerDashboard and PublicPlayerDashboard.

   Props
   ─────
   careerHighs  – array of { stat, value, date, season, opponent }
   ddGames      – array of DD game log entries (for double-doubles card)
   tdGames      – array of TD game log entries (for triple-doubles card)
   loading      – boolean
   ──────────────────────────────────────────────────────────────────────── */
import React, { useState } from "react";
import { Zap, ChevronDown, ChevronUp } from "lucide-react";

/* ── Stat category config ─────────────────────────────────────────────── */
export const CAREER_HIGH_STATS = [
  { key: "points", label: "Points" },
  { key: "rebounds", label: "Rebounds" },
  { key: "assists", label: "Assists" },
  { key: "three_pm", label: "3-Pointers" },
  { key: "ft_made", label: "Free Throws" },
  { key: "two_pm", label: "2-Pointers" },
];

/* ── Helper: format a game_date string to a readable date ─────────────── */
const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

/* ── Single career-high card (per-game peak) ──────────────────────────── */
const CareerHighCard = ({ label, value, date, season, opponent }) => {
  const hasData = value != null && value > 0;

  return (
    <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5 flex flex-col gap-2 overflow-hidden">
      {/* Accent stripe */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 to-amber-400 rounded-t-2xl" />

      {/* Label */}
      <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
        {label}
      </span>

      {hasData ? (
        <>
          {/* Peak value */}
          <div className="text-4xl font-bold tracking-tight dark:text-white leading-none">
            {value}
          </div>

          {/* Context */}
          <div className="flex flex-col gap-0.5 mt-1">
            {opponent && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                vs{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {opponent}
                </span>
              </p>
            )}
            {date && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {fmtDate(date)}
              </p>
            )}
            {season && (
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                {season}
              </p>
            )}
          </div>

          {/* Badge */}
          <span className="mt-auto self-start inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-900">
            <Zap size={9} />
            Single-Game High
          </span>
        </>
      ) : (
        <div className="flex-1 flex items-center">
          <p className="text-sm text-gray-400 dark:text-gray-600 italic">
            No record yet
          </p>
        </div>
      )}
    </div>
  );
};

/* ── Collapsible game-log row ─────────────────────────────────────────── */
const GameLogRow = ({ game }) => (
  <div className="py-2.5 border-t border-gray-100 dark:border-gray-800 first:border-t-0">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        {game.opponent && (
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
            vs {game.opponent}
          </p>
        )}
        <div className="flex flex-wrap gap-x-2 mt-0.5">
          {game.date && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500">
              {fmtDate(game.date)}
            </span>
          )}
          {game.season && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500">
              · {game.season}
            </span>
          )}
        </div>
      </div>
      {/* Stat pills */}
      <div className="flex flex-wrap gap-1 shrink-0">
        {game.pts >= 10 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400">
            {game.pts} PTS
          </span>
        )}
        {game.reb >= 10 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
            {game.reb} REB
          </span>
        )}
        {game.ast >= 10 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
            {game.ast} AST
          </span>
        )}
        {/* Show non-10+ stats in gray for context */}
        {game.pts > 0 && game.pts < 10 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            {game.pts} PTS
          </span>
        )}
        {game.reb > 0 && game.reb < 10 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            {game.reb} REB
          </span>
        )}
        {game.ast > 0 && game.ast < 10 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            {game.ast} AST
          </span>
        )}
      </div>
    </div>
  </div>
);

/* ── Double-Double / Triple-Double card (full-width, collapsible) ──────── */
const MultiStatCard = ({ label, count, games, accentClass, badgeLabel }) => {
  const [expanded, setExpanded] = useState(false);
  const hasData = count > 0;
  const preview = games.slice(0, 3);
  const rest = games.slice(3);

  return (
    <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden col-span-2 sm:col-span-3 lg:col-span-2">
      {/* Accent stripe */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${accentClass}`} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            {label}
          </span>
          {hasData && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-900">
              <Zap size={9} />
              {badgeLabel}
            </span>
          )}
        </div>

        {hasData ? (
          <>
            {/* Count */}
            <div className="text-4xl font-bold tracking-tight dark:text-white leading-none mb-4">
              {count}
              <span className="text-base font-normal text-gray-400 dark:text-gray-500 ml-2">
                career
              </span>
            </div>

            {/* Game log — always show first 3 */}
            <div>
              {preview.map((g, i) => (
                <GameLogRow key={i} game={g} />
              ))}

              {/* Expandable rest */}
              {rest.length > 0 && (
                <>
                  {expanded &&
                    rest.map((g, i) => (
                      <GameLogRow key={`rest-${i}`} game={g} />
                    ))}
                  <button
                    onClick={() => setExpanded((v) => !v)}
                    className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-orange-600 dark:text-orange-400 hover:underline"
                  >
                    {expanded ? (
                      <>
                        <ChevronUp size={13} /> Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown size={13} /> Show {rest.length} more
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-600 italic">
            No {label.toLowerCase()} recorded yet.
          </p>
        )}
      </div>
    </div>
  );
};

/* ── Skeleton loader ──────────────────────────────────────────────────── */
const CareerHighsSkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
    {Array.from({ length: 9 }).map((_, i) => (
      <div
        key={i}
        className={`bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse ${
          i >= 7 ? "h-48 col-span-2 sm:col-span-3 lg:col-span-2" : "h-36"
        }`}
      />
    ))}
  </div>
);

/* ── Main exported component ──────────────────────────────────────────── */
const CareerHighsSection = ({ careerHighs, ddGames, tdGames, loading }) => {
  const hasAnyData =
    (careerHighs && careerHighs.length > 0) ||
    (ddGames && ddGames.length > 0) ||
    (tdGames && tdGames.length > 0);

  return (
    <div>
      {loading ? (
        <CareerHighsSkeleton />
      ) : !hasAnyData ? (
        <div className="text-center py-10 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800">
          <Zap
            size={32}
            className="mx-auto mb-3 text-gray-300 dark:text-gray-700"
          />
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            No career highs recorded yet.
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-600 mt-1">
            Career highs will appear here once games are logged.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Per-stat single-game highs */}
          {CAREER_HIGH_STATS.map(({ key, label }) => {
            const entry = (careerHighs || []).find((h) => h.stat === key);
            return (
              <CareerHighCard
                key={key}
                label={label}
                value={entry?.value ?? null}
                date={entry?.date ?? null}
                season={entry?.season ?? null}
                opponent={entry?.opponent ?? null}
              />
            );
          })}

          {/* Double-Doubles */}
          <MultiStatCard
            label="Double-Doubles"
            count={(ddGames || []).length}
            games={ddGames || []}
            accentClass="bg-gradient-to-r from-blue-500 to-indigo-400"
            badgeLabel="Career Total"
          />

          {/* Triple-Doubles */}
          <MultiStatCard
            label="Triple-Doubles"
            count={(tdGames || []).length}
            games={tdGames || []}
            accentClass="bg-gradient-to-r from-purple-500 to-violet-400"
            badgeLabel="Career Total"
          />
        </div>
      )}
    </div>
  );
};

export default CareerHighsSection;

/* ── Data-fetching helper (shared by both dashboards) ─────────────────────
   Returns:
     {
       highs:   [{ stat, value, date, season, opponent }],
       ddGames: [{ date, season, opponent, pts, reb, ast }],
       tdGames: [{ date, season, opponent, pts, reb, ast }],
     }

   Fetches all game_stats for the given player_ids (counts_in_standings only),
   enriches each row with computed points, opponent name, and season name,
   then derives per-stat peaks and DD/TD game logs.
   ──────────────────────────────────────────────────────────────────────── */
export const fetchCareerHighs = async (supabase, playerIds) => {
  const empty = { highs: [], ddGames: [], tdGames: [] };
  if (!playerIds || playerIds.length === 0) return empty;

  const { data: rows, error } = await supabase
    .from("game_stats")
    .select(
      `
      player_id,
      two_pm,
      three_pm,
      ft_made,
      rebounds,
      assists,
      steals,
      games!inner(
        id,
        game_date,
        counts_in_standings,
        team1_id,
        team2_id,
        season_id,
        team1:teams!games_team1_id_fkey(id, name),
        team2:teams!games_team2_id_fkey(id, name),
        season:seasons!games_season_id_fkey(id, name)
      ),
      players!inner(team_id)
    `,
    )
    .in("player_id", playerIds)
    .eq("games.counts_in_standings", true);

  if (error) {
    console.error("fetchCareerHighs error:", error);
    return empty;
  }

  // Filter out DNP rows (all zeros)
  const active = (rows || []).filter((r) => {
    const pts = (r.two_pm ?? 0) * 2 + (r.three_pm ?? 0) * 3 + (r.ft_made ?? 0);
    return (
      pts > 0 ||
      (r.rebounds ?? 0) > 0 ||
      (r.assists ?? 0) > 0 ||
      (r.steals ?? 0) > 0 ||
      (r.three_pm ?? 0) > 0 ||
      (r.ft_made ?? 0) > 0 ||
      (r.two_pm ?? 0) > 0
    );
  });

  // Enrich each row with derived fields
  const enriched = active.map((r) => {
    const g = r.games;
    const playerTeamId = r.players?.team_id;
    const pts = (r.two_pm ?? 0) * 2 + (r.three_pm ?? 0) * 3 + (r.ft_made ?? 0);
    const reb = r.rebounds ?? 0;
    const ast = r.assists ?? 0;
    const stl = r.steals ?? 0;

    let opponent = null;
    if (g) {
      if (playerTeamId === g.team1_id) opponent = g.team2?.name ?? null;
      else if (playerTeamId === g.team2_id) opponent = g.team1?.name ?? null;
      else if (g.team1?.name && g.team2?.name)
        opponent = `${g.team1.name} vs ${g.team2.name}`;
    }

    // Count categories ≥ 10 for DD/TD detection
    const cats10 =
      (pts >= 10 ? 1 : 0) + (reb >= 10 ? 1 : 0) + (ast >= 10 ? 1 : 0);

    return {
      _pts: pts,
      _reb: reb,
      _ast: ast,
      _stl: stl,
      _three_pm: r.three_pm ?? 0,
      _ft_made: r.ft_made ?? 0,
      _two_pm: r.two_pm ?? 0,
      _opponent: opponent,
      _date: g?.game_date ?? null,
      _season: g?.season?.name ?? null,
      _cats10: cats10,
    };
  });

  // ── Per-stat single-game highs ─────────────────────────────────────────
  const statGetters = {
    points: (r) => r._pts,
    rebounds: (r) => r._reb,
    assists: (r) => r._ast,
    steals: (r) => r._stl,
    three_pm: (r) => r._three_pm,
    ft_made: (r) => r._ft_made,
    two_pm: (r) => r._two_pm,
  };

  const highs = [];
  for (const [stat, getter] of Object.entries(statGetters)) {
    let best = null;
    let bestVal = -1;
    for (const row of enriched) {
      const val = getter(row);
      if (val > bestVal) {
        bestVal = val;
        best = row;
      }
    }
    if (best && bestVal > 0) {
      highs.push({
        stat,
        value: bestVal,
        date: best._date,
        season: best._season,
        opponent: best._opponent,
      });
    }
  }

  // ── Double-doubles & triple-doubles game logs ──────────────────────────
  const ddGames = enriched
    .filter((r) => r._cats10 >= 2)
    .map((r) => ({
      date: r._date,
      season: r._season,
      opponent: r._opponent,
      pts: r._pts,
      reb: r._reb,
      ast: r._ast,
    }))
    .sort((a, b) => {
      // Most recent first
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date) - new Date(a.date);
    });

  const tdGames = enriched
    .filter((r) => r._cats10 >= 3)
    .map((r) => ({
      date: r._date,
      season: r._season,
      opponent: r._opponent,
      pts: r._pts,
      reb: r._reb,
      ast: r._ast,
    }))
    .sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date) - new Date(a.date);
    });

  return { highs, ddGames, tdGames };
};
